import { useEffect, useRef } from 'react';

import { analytics } from '../utilities/analytics';
import { ListingReadyParams } from '../utilities/analytics.types';

interface ListingReadyTrackingOptions extends Omit<ListingReadyParams, 'wait_ms'> {
  hasItems: boolean;
  isFetching: boolean;
  searchKey: string;
}

type SearchWait = {
  searchKey: string;
  loadingMs: number;
  loadingSince: number | null;
  isTracked: boolean;
};

export function useListingReadyTracking({
  hasItems,
  isFetching,
  searchKey,
  item_list_name,
  package_count,
  package_checks_timed_out,
  full_package_check,
}: ListingReadyTrackingOptions): void {
  const wait = useRef<SearchWait | null>(null);

  useEffect(() => {
    if (wait.current?.searchKey !== searchKey) {
      wait.current = { searchKey, loadingMs: 0, loadingSince: null, isTracked: false };
    }
    const current = wait.current;
    if (current.isTracked) return;

    if (isFetching) {
      current.loadingSince ??= performance.now();
      return;
    }
    if (current.loadingSince !== null) {
      current.loadingMs += performance.now() - current.loadingSince;
      current.loadingSince = null;
    }
    if (!hasItems) return;

    analytics.track({
      event: 'listing_ready',
      params: {
        item_list_name,
        wait_ms: Math.round(current.loadingMs),
        package_count,
        package_checks_timed_out,
        full_package_check,
      },
    });
    current.isTracked = true;
  }, [
    hasItems,
    isFetching,
    searchKey,
    item_list_name,
    package_count,
    package_checks_timed_out,
    full_package_check,
  ]);
}
